// src/Pages/Register.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import StyledSwitch from "../Components/Layout/StyledSwitch";
import { ChevronRight, ChevronDown, ArrowLeft, Building2, Plus, Eye, EyeOff } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import LanguageSelector from '../Components/Layout/LanguageSelector';
import logo from '../images/pslogo.png';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [createNewCompany, setCreateNewCompany] = useState(false);
    const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: '',
        department: '',
        companyCode: '',
        newsupdates: false,
        company: {
            name: '',
            address: '',
            zipcode: '',
            city: '',
            phonenumber: ''
        }
    });
    const [fieldErrors, setFieldErrors] = useState({});

    const roles = [
        { id: 'owner', name: t('profile.roles.owner') || 'Eigenaar' },
        { id: 'chef', name: t('profile.roles.chef') || 'Chef' },
        { id: 'manager', name: t('profile.roles.manager') || 'Manager' },
        { id: 'employee', name: t('profile.roles.employee') || 'Werknemer' },
    ];

    const departments = [
        { id: 'kitchen', name: t('profile.departments.kitchen') || 'Keuken' },
        { id: 'service', name: t('profile.departments.service') || 'Service' },
    ];

    const validateField = (name, value) => {
        switch (name) {
            case 'firstname':
            case 'lastname':
                if (!value.trim()) {
                    return t('validation.fieldRequired', { field: t(`profile.${name}`) });
                }
                if (value.trim().length < 2) {
                    return t('validation.minLength', { field: t(`profile.${name}`), length: 2 });
                }
                return '';

            case 'email':
                if (!value.trim()) {
                    return t('validation.emailRequired');
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return t('validation.invalidEmail');
                }
                return '';

            case 'password':
                if (!value) {
                    return t('validation.passwordRequired');
                }
                if (value.length < 8) {
                    return t('validation.passwordMinLength');
                }
                if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
                    return t('validation.passwordStrength') || 'Wachtwoord moet minimaal 1 hoofdletter, 1 kleine letter en 1 cijfer bevatten';
                }
                return '';

            case 'confirmPassword':
                if (!value) {
                    return t('validation.confirmPasswordRequired') || 'Bevestiging wachtwoord is verplicht';
                }
                if (value !== formData.password) {
                    return t('validation.passwordsDoNotMatch');
                }
                return '';

            case 'role':
                if (!value) {
                    return t('validation.roleRequired') || 'Rol is verplicht';
                }
                return '';

            case 'department':
                if (!value) {
                    return t('validation.departmentRequired') || 'Afdeling is verplicht';
                }
                return '';

            case 'companyCode':
                if (!createNewCompany && !value.trim()) {
                    return t('validation.companyCodeRequired') || 'Bedrijfscode is verplicht';
                }
                return '';

            case 'company.name':
                if (createNewCompany && !value.trim()) {
                    return t('validation.companyNameRequired') || 'Bedrijfsnaam is verplicht';
                }
                return '';

            case 'company.address':
                if (createNewCompany && !value.trim()) {
                    return t('validation.addressRequired') || 'Adres is verplicht';
                }
                return '';

            case 'company.zipcode':
                if (createNewCompany && !value.trim()) {
                    return t('validation.zipcodeRequired') || 'Postcode is verplicht';
                }
                return '';

            case 'company.city':
                if (createNewCompany && !value.trim()) {
                    return t('validation.cityRequired') || 'Plaats is verplicht';
                }
                return '';

            case 'company.phonenumber':
                if (createNewCompany && !value.trim()) {
                    return t('validation.phoneRequired') || 'Telefoonnummer is verplicht';
                }
                return '';

            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name.startsWith('company.')) {
            const companyField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                company: {
                    ...prev.company,
                    [companyField]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear errors when user types
        setError('');
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }

        // Revalidate confirm password when password changes
        if (name === 'password' && formData.confirmPassword) {
            const confirmError = validateField('confirmPassword', formData.confirmPassword);
            setFieldErrors(prev => ({
                ...prev,
                confirmPassword: confirmError
            }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const error = validateField(name, value);
        setFieldErrors(prev => ({
            ...prev,
            [name]: error
        }));
    };

    const validateForm = () => {
        const errors = {};
        
        // Validate all required fields
        errors.firstname = validateField('firstname', formData.firstname);
        errors.lastname = validateField('lastname', formData.lastname);
        errors.email = validateField('email', formData.email);
        errors.password = validateField('password', formData.password);
        errors.confirmPassword = validateField('confirmPassword', formData.confirmPassword);
        errors.role = validateField('role', formData.role);
        errors.department = validateField('department', formData.department);

        if (createNewCompany) {
            errors['company.name'] = validateField('company.name', formData.company.name);
            errors['company.address'] = validateField('company.address', formData.company.address);
            errors['company.zipcode'] = validateField('company.zipcode', formData.company.zipcode);
            errors['company.city'] = validateField('company.city', formData.company.city);
            errors['company.phonenumber'] = validateField('company.phonenumber', formData.company.phonenumber);
        } else {
            errors.companyCode = validateField('companyCode', formData.companyCode);
        }

        setFieldErrors(errors);

        // Return true if no errors
        return Object.values(errors).every(error => !error);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const payload = {
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                email: formData.email.trim(),
                password: formData.password,
                role: formData.role,
                department: formData.department,
                newsupdates: formData.newsupdates
            };

            if (createNewCompany) {
                payload.company = {
                    name: formData.company.name.trim(),
                    address: formData.company.address.trim(),
                    zipCode: formData.company.zipcode.trim(),
                    city: formData.company.city.trim(),
                    phoneNumber: formData.company.phonenumber.trim()
                };
            } else {
                payload.companyCode = formData.companyCode.trim();
            }

            const response = await axiosInstance.post('/auth/Register', payload);
            
            if (response.data.success) {
                setSuccess(t('auth.registrationSuccess') || 'U ontvangt binnen enkele minuten een e-mail met activatiegegevens voor je account.');
                
                // Reset form
                setFormData({
                    firstname: '',
                    lastname: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    role: '',
                    department: '',
                    companyCode: '',
                    newsupdates: false,
                    company: {
                        name: '',
                        address: '',
                        zipcode: '',
                        city: '',
                        phonenumber: ''
                    }
                });
                setFieldErrors({});
            } else {
                setError(response.data.error || t('auth.registrationError') || 'Er ging iets mis bij het registreren.');
            }
        } catch (err) {
            console.log('Registration error:', err);
            if (err.response?.data) {
                setError(err.response.data || t('auth.registrationError'));
            } else if (err.request) {
                setError(t('auth.connectionError') || 'Kan geen verbinding maken met de server.');
            } else {
                setError(t('auth.registrationError') || 'Er ging iets mis bij het registreren.');
            }
        } finally {
            setIsLoading(false);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        } 
    };

    const renderFieldError = (fieldName) => {
        const error = fieldErrors[fieldName];
        return error ? (
            <p className="text-red-500 text-sm mt-1 font-inter">{error}</p>
        ) : null;
    };

    const handleCompanyTypeChange = (isNew) => {
        setCreateNewCompany(isNew);
        // Clear relevant field errors when switching company types
        if (isNew) {
            setFieldErrors(prev => ({ ...prev, companyCode: '' }));
        } else {
            setFieldErrors(prev => ({
                ...prev,
                'company.name': '',
                'company.address': '',
                'company.zipcode': '',
                'company.city': '',
                'company.phonenumber': ''
            }));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="max-w-2xl w-full space-y-6 p-8 bg-white rounded-lg shadow-lg relative">
                {/* Logo */}
                <div className="absolute left-[15px] top-[-5px] w-[80px]">
                    <img src={logo} alt="PS Logo" />
                </div>
                
                {/* Language Selector */}
                <div className="absolute right-4 top-4">
                    <LanguageSelector />
                </div>

                {/* Header Section */}
                <div className="text-center pt-8">
                    <h1 className="text-3xl font-inter text-ps-blue-dark mb-2">
                        {t('auth.register')}
                    </h1>
                    <p className="text-sm text-gray-600 font-inter">
                        {t('auth.registerSubtitle') || 'Maak een nieuw account aan'}
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    {/* Error/Success Messages */}
                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg font-inter">
                            <div className="flex items-start">
                                <div className="flex-1">{error}</div>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="p-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg font-inter">
                            <div className="flex items-start">
                                <div className="flex-1">{success}</div>
                            </div>
                        </div>
                    )}

                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="firstname" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.firstName')} <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="firstname"
                                type="text"
                                name="firstname"
                                required
                                value={formData.firstname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                    fieldErrors.firstname 
                                        ? 'border-red-500 focus:border-red-500' 
                                        : 'border-gray-300 focus:border-ps-blue-dark'
                                }`}
                                disabled={isLoading}
                                autoComplete="given-name"
                                placeholder={t('profile.firstNamePlaceholder') || ''}
                            />
                            {renderFieldError('firstname')}
                        </div>
                        
                        <div>
                            <label htmlFor="lastname" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.lastName')} <span className="text-red-500">*</span>
                            </label>
                            <Input
                                id="lastname"
                                type="text"
                                name="lastname"
                                required
                                value={formData.lastname}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                    fieldErrors.lastname 
                                        ? 'border-red-500 focus:border-red-500' 
                                        : 'border-gray-300 focus:border-ps-blue-dark'
                                }`}
                                disabled={isLoading}
                                autoComplete="family-name"
                                placeholder={t('profile.lastNamePlaceholder') || ''}
                            />
                            {renderFieldError('lastname')}
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-inter text-ps-blue-dark mb-2">
                            {t('auth.email')} <span className="text-red-500">*</span>
                        </label>
                        <Input
                            id="email"
                            type="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                fieldErrors.email 
                                    ? 'border-red-500 focus:border-red-500' 
                                    : 'border-gray-300 focus:border-ps-blue-dark'
                            }`}
                            placeholder={t('auth.emailPlaceholder') || 'naam@bedrijf.nl'}
                            disabled={isLoading}
                            autoComplete="email"
                        />
                        {renderFieldError('email')}
                    </div>

                    {/* Passwords */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('auth.password')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    minLength={8}
                                    required
                                    value={formData.password}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={`w-full p-3 border rounded-md bg-white font-inter pr-12 transition-colors ${
                                        fieldErrors.password 
                                            ? 'border-red-500 focus:border-red-500' 
                                            : 'border-gray-300 focus:border-ps-blue-dark'
                                    }`}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    disabled={isLoading}
                                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {renderFieldError('password')}
                        </div>
                        
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('auth.confirmPassword')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    minLength={8}
                                    required
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={`w-full p-3 border rounded-md bg-white font-inter pr-12 transition-colors ${
                                        fieldErrors.confirmPassword 
                                            ? 'border-red-500 focus:border-red-500' 
                                            : 'border-gray-300 focus:border-ps-blue-dark'
                                    }`}
                                    disabled={isLoading}
                                    autoComplete="new-password"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    disabled={isLoading}
                                    aria-label={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                                >
                                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                            {renderFieldError('confirmPassword')}
                        </div>
                    </div>

                    {/* Role and Department */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="relative">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.role')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className={`w-full flex items-center justify-between p-3 border rounded-md bg-white text-left font-inter transition-colors ${
                                        fieldErrors.role 
                                            ? 'border-red-500 focus:border-red-500' 
                                            : 'border-gray-300 focus:border-ps-blue-dark hover:border-gray-400'
                                    }`}
                                    onClick={() => {
                                        setIsRoleDropdownOpen(!isRoleDropdownOpen);
                                        setIsDepartmentDropdownOpen(false);
                                    }}
                                    disabled={isLoading}
                                    aria-haspopup="listbox"
                                    aria-expanded={isRoleDropdownOpen}
                                >
                                    <span className={formData.role ? 'text-gray-900' : 'text-gray-500'}>
                                        {roles.find(r => r.id === formData.role)?.name || t('auth.selectRole') || "Selecteer een rol"}
                                    </span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isRoleDropdownOpen && (
                                    <>
                                        <div 
                                            className="fixed inset-0 z-10" 
                                            onClick={() => setIsRoleDropdownOpen(false)}
                                        />
                                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                            {roles.map((role) => (
                                                <div
                                                    key={role.id}
                                                    className="p-3 hover:bg-gray-100 cursor-pointer font-inter transition-colors"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, role: role.id }));
                                                        setIsRoleDropdownOpen(false);
                                                        if (fieldErrors.role) {
                                                            setFieldErrors(prev => ({ ...prev, role: '' }));
                                                        }
                                                    }}
                                                >
                                                    {role.name}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            {renderFieldError('role')}
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.department')} <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className={`w-full flex items-center justify-between p-3 border rounded-md bg-white text-left font-inter transition-colors ${
                                        fieldErrors.department 
                                            ? 'border-red-500 focus:border-red-500' 
                                            : 'border-gray-300 focus:border-ps-blue-dark hover:border-gray-400'
                                    }`}
                                    onClick={() => {
                                        setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen);
                                        setIsRoleDropdownOpen(false);
                                    }}
                                    disabled={isLoading}
                                    aria-haspopup="listbox"
                                    aria-expanded={isDepartmentDropdownOpen}
                                >
                                    <span className={formData.department ? 'text-gray-900' : 'text-gray-500'}>
                                        {departments.find(d => d.id === formData.department)?.name || t('auth.selectDepartment') || "Selecteer een afdeling"}
                                    </span>
                                    <ChevronDown className={`h-4 w-4 transition-transform ${isDepartmentDropdownOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isDepartmentDropdownOpen && (
                                    <>
                                        <div 
                                            className="fixed inset-0 z-10" 
                                            onClick={() => setIsDepartmentDropdownOpen(false)}
                                        />
                                        <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                                            {departments.map((department) => (
                                                <div
                                                    key={department.id}
                                                    className="p-3 hover:bg-gray-100 cursor-pointer font-inter transition-colors"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, department: department.id }));
                                                        setIsDepartmentDropdownOpen(false);
                                                        if (fieldErrors.department) {
                                                            setFieldErrors(prev => ({ ...prev, department: '' }));
                                                        }
                                                    }}
                                                >
                                                    {department.name}
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                            {renderFieldError('department')}
                        </div>
                    </div>

                    {/* Company Section */}
                    <div className="pt-4">
                        <h3 className="text-lg font-inter text-ps-blue-dark mb-4">
                            {t('auth.companyInformation')}
                        </h3>
                        
                        <div className="flex gap-4 mb-6">
                            <Button
                                type="button"
                                onClick={() => handleCompanyTypeChange(false)}
                                className={`flex-1 flex items-center justify-center gap-2 h-12 font-inter transition-all ${
                                    !createNewCompany 
                                        ? 'bg-ps-blue-dark text-white hover:bg-ps-blue-dark/90' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                disabled={isLoading}
                            >
                                <Building2 className="h-4 w-4" />
                                {t('auth.joinExistingCompany') || 'Bestaand bedrijf'}
                            </Button>
                            <Button
                                type="button"
                                onClick={() => handleCompanyTypeChange(true)}
                                className={`flex-1 flex items-center justify-center gap-2 h-12 font-inter transition-all ${
                                    createNewCompany 
                                        ? 'bg-ps-blue-dark text-white hover:bg-ps-blue-dark/90' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                disabled={isLoading}
                            >
                                <Plus className="h-4 w-4" />
                                {t('auth.createNewCompany') || 'Nieuw bedrijf'}
                            </Button>
                        </div>

                        {createNewCompany ? (
                            <div className="space-y-4 bg-blue-50 p-6 rounded-lg border border-blue-100">
                                <h4 className="font-medium text-ps-blue-dark font-inter mb-4 flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    {t('auth.newCompanyDetails') || 'Nieuwe bedrijfsgegevens'}
                                </h4>
                                
                                <div>
                                    <label htmlFor="companyName" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.companyName')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="companyName"
                                        type="text"
                                        name="company.name"
                                        required
                                        value={formData.company.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                            fieldErrors['company.name'] 
                                                ? 'border-red-500 focus:border-red-500' 
                                                : 'border-gray-300 focus:border-ps-blue-dark'
                                        }`}
                                        disabled={isLoading}
                                        autoComplete="organization"
                                        placeholder={t('profile.companyNamePlaceholder') || ''}
                                    />
                                    {renderFieldError('company.name')}
                                </div>
                                
                                <div>
                                    <label htmlFor="companyAddress" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.address')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="companyAddress"
                                        type="text"
                                        name="company.address"
                                        required
                                        value={formData.company.address}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                            fieldErrors['company.address'] 
                                                ? 'border-red-500 focus:border-red-500' 
                                                : 'border-gray-300 focus:border-ps-blue-dark'
                                        }`}
                                        disabled={isLoading}
                                        autoComplete="street-address"
                                        placeholder={t('profile.addressPlaceholder') || ''}
                                    />
                                    {renderFieldError('company.address')}
                                </div>
                                
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="col-span-1">
                                        <label htmlFor="companyZipcode" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            {t('profile.zipcode')} <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            id="companyZipcode"
                                            type="text"
                                            name="company.zipcode"
                                            required
                                            value={formData.company.zipcode}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                                fieldErrors['company.zipcode'] 
                                                    ? 'border-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 focus:border-ps-blue-dark'
                                            }`}
                                            disabled={isLoading}
                                            autoComplete="postal-code"
                                            placeholder="1234 AB"
                                        />
                                        {renderFieldError('company.zipcode')}
                                    </div>
                                    <div className="col-span-3">
                                        <label htmlFor="companyCity" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            {t('profile.city')} <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            id="companyCity"
                                            type="text"
                                            name="company.city"
                                            required
                                            value={formData.company.city}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                                fieldErrors['company.city'] 
                                                    ? 'border-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 focus:border-ps-blue-dark'
                                            }`}
                                            disabled={isLoading}
                                            autoComplete="address-level2"
                                            placeholder={t('profile.cityPlaceholder') || ''}
                                        />
                                        {renderFieldError('company.city')}
                                    </div>
                                </div>
                                
                                <div>
                                    <label htmlFor="companyPhone" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.phoneNumber')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="companyPhone"
                                        type="tel"
                                        name="company.phonenumber"
                                        required
                                        value={formData.company.phonenumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                            fieldErrors['company.phonenumber'] 
                                                ? 'border-red-500 focus:border-red-500' 
                                                : 'border-gray-300 focus:border-ps-blue-dark'
                                        }`}
                                        disabled={isLoading}
                                        autoComplete="tel"
                                        placeholder={t('profile.phoneNumberPlaceholder') || '+31 6 12345678'}
                                    />
                                    {renderFieldError('company.phonenumber')}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                                <h4 className="font-medium text-ps-blue-dark font-inter mb-4 flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    {t('auth.existingCompanyDetails') || 'Bestaand bedrijf'}
                                </h4>
                                <div>
                                    <label htmlFor="companyCode" className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('auth.companyCode')} <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        id="companyCode"
                                        type="text"
                                        name="companyCode"
                                        required
                                        value={formData.companyCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className={`w-full p-3 border rounded-md bg-white font-inter transition-colors ${
                                            fieldErrors.companyCode 
                                                ? 'border-red-500 focus:border-red-500' 
                                                : 'border-gray-300 focus:border-ps-blue-dark'
                                        }`}
                                        placeholder={t('auth.companyCodePlaceholder') || 'Voer de code in die je van je bedrijf hebt ontvangen'}
                                        disabled={isLoading}
                                        autoComplete="organization"
                                    />
                                    {renderFieldError('companyCode')}
                                    <p className="text-xs text-gray-600 mt-1 font-inter">
                                        {t('auth.companyCodeHelp') || 'Deze code heb je ontvangen van je bedrijf om deel te nemen aan hun menukaart systeem.'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Newsletter Opt-in */}
                    <div className="flex items-start space-x-3 py-4 bg-gray-50 p-4 rounded-lg">
                        <div className="flex-shrink-0 mt-1">
                            <StyledSwitch
                                id="newsupdates"
                                checked={formData.newsupdates}
                                onCheckedChange={(checked) =>
                                    setFormData(prev => ({ ...prev, newsupdates: checked }))
                                }
                            />
                        </div>
                        <div className="flex-1">
                            <label
                                htmlFor="newsupdates"
                                className="font-inter text-sm text-ps-blue-dark cursor-pointer block"
                            >
                                {t('profile.newsUpdates')}
                            </label>
                            <p className="text-xs text-gray-600 mt-1 font-inter">
                                {t('auth.newsUpdatesDescription') || 'Ontvang informatie over nieuwe functies en updates van PS Menu Maker.'}
                            </p>
                        </div>
                    </div>

                    {/* Submit & Back Buttons */}
                    <div className="flex flex-col space-y-4 pt-6">
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white flex items-center justify-center gap-2 h-12 font-inter transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    {t('auth.registering') || 'Bezig met registreren...'}
                                </>
                            ) : (
                                <>
                                    {t('auth.register')}
                                    <ChevronRight className="h-4 w-4" />
                                </>
                            )}
                        </Button>

                        <div className="text-center">
                            <Button
                                type="button"
                                variant="link"
                                onClick={() => navigate('/')}
                                className="text-ps-blue-dark hover:text-ps-blue font-medium flex items-center justify-center gap-2 p-0 h-auto"
                                disabled={isLoading}
                            >
                                <ArrowLeft className="h-4 w-4" />
                                {t('auth.backToLogin')}
                            </Button>
                        </div>
                    </div>

                    {/* Terms and Privacy Notice */}
                    <div className="text-center pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-600 font-inter">
                            {t('auth.termsNotice') || 'Door te registreren ga je akkoord met onze'}{' '}
                            <button
                                type="button"
                                onClick={() => window.open('/terms', '_blank')}
                                className="text-ps-blue-dark hover:text-ps-blue underline"
                                disabled={isLoading}
                            >
                                {t('auth.termsOfService') || 'Algemene voorwaarden'}
                            </button>
                            {' '}{t('auth.and') || 'en'}{' '}
                            <button
                                type="button"
                                onClick={() => window.open('/privacy', '_blank')}
                                className="text-ps-blue-dark hover:text-ps-blue underline"
                                disabled={isLoading}
                            >
                                {t('auth.privacyPolicy') || 'Privacy beleid'}
                            </button>
                            .
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Register;